import { useCallback, useMemo } from "react"

import type { DashboardUseCase, UseCaseData, UseCaseFilters } from "../types/use-case.types"

import { useUseCasesStore } from "../../../stores/use-cases-store"
import { FEASIBILITY_MAP, STATUSES } from "../types/use-case.types"
import { calculateFromUseCaseData } from "../utils/financial-calculations"

function resolveSites(data: UseCaseData): string[] {
  if (Array.isArray(data.siteAreaSel) && data.siteAreaSel.length) {
    return data.siteAreaSel.map((s) => s.trim()).filter(Boolean)
  }
  return ["—"]
}

function toDashboardUseCase(uc: UseCaseData): DashboardUseCase {
  const kpis = calculateFromUseCaseData(uc)
  const statIdx = Math.max(0, Math.min(3, parseInt(String(uc.statIdx)) || 0))
  const status = STATUSES[statIdx]
  const feasV = String(uc.feasV)
  const feas = FEASIBILITY_MAP[feasV] ?? { label: "Unknown", className: "" }
  const sites = resolveSites(uc)

  return {
    ...uc,
    ...kpis,
    statusText: status.text,
    statusClassName: status.className,
    feasLabel: feas.label,
    feasClassName: feas.className,
    sites,
  }
}

export function useUseCaseFilters(useCases: UseCaseData[] | undefined) {
  const { filters, setFilters } = useUseCasesStore()

  const dashboardUseCases = useMemo(() => (useCases ?? []).map(toDashboardUseCase), [useCases])

  const allSites = useMemo(() => {
    const siteSet = new Set<string>()
    dashboardUseCases.forEach((uc) => uc.sites.forEach((s) => siteSet.add(s)))
    return [...siteSet].sort()
  }, [dashboardUseCases])

  const filteredUseCases = useMemo(() => {
    return dashboardUseCases.filter((uc) => {
      if (filters.statuses.size > 0 && !filters.statuses.has(uc.statusText)) return false
      if (filters.tenkCategories.size > 0 && !filters.tenkCategories.has(uc.tenkCategory))
        return false
      if (filters.feasibilities.size > 0 && !filters.feasibilities.has(String(uc.feasV)))
        return false
      if (filters.sites.size > 0 && !uc.sites.some((s) => filters.sites.has(s))) return false
      if (filters.searchText) {
        const haystack = [
          uc.ucName,
          ...uc.sites,
          ...uc.efSel,
          ...uc.bpSel,
          ...uc.saSel,
          uc.statusText,
        ]
          .join(" ")
          .toLowerCase()
        if (!haystack.includes(filters.searchText.toLowerCase())) return false
      }
      return true
    })
  }, [dashboardUseCases, filters])

  const updateFilter = useCallback(
    (key: keyof UseCaseFilters, value: unknown) => {
      setFilters({ ...filters, [key]: value })
    },
    [filters, setFilters]
  )

  const clearFilters = useCallback(() => {
    setFilters({
      statuses: new Set(),
      tenkCategories: new Set(),
      feasibilities: new Set(),
      sites: new Set(),
      searchText: "",
    })
  }, [setFilters])

  return {
    dashboardUseCases,
    filteredUseCases,
    allSites,
    filters,
    updateFilter,
    clearFilters,
  }
}
